import React, { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import { Formik, Form } from "formik";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Select from "@mui/material/Select";
import { Box, Typography } from "@mui/material";
import { PersonOffRounded, WarningAmberRounded } from "@mui/icons-material";
import { BaseModal } from "./BaseModal";
import { PatientSummaryCard } from "./PatientSummaryCard";
import axios from "axios";

export const InactivatePatientModal = ({
  open,
  setOpen,
  handleClose,
  patient,
  setReason,
  reason,
}) => {
  const [reasons, setReasons] = useState([]);
  const getUnsubscribeReason = async () => {
    return await axios
      .get(
        `${process.env.REACT_APP_BASE_URL}/services/parametrics/api/unsubscribe-reasons`
      )
      .then((response) => {
        setReasons(response.data);
      })
      .catch((error) => {
        console.log(error.response.data);
      });
  };
  useEffect(() => {
    getUnsubscribeReason();
  }, []);

  return (
    <BaseModal
      width="600px"
      open={open}
      setOpen={() => {
        setReason("");
        setOpen(!open);
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
        {/* Header de acción (rojo) */}
        <Box
          sx={{
            background: "linear-gradient(195deg, #DA2037, #8e131f)",
            borderRadius: "15px",
            color: "white",
            padding: "1.25rem 1.5rem",
            boxShadow: "0 8px 24px rgba(218,32,55,0.25)",
            display: "flex",
            alignItems: "center",
            gap: "1rem",
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "12px",
              background: "rgba(255,255,255,0.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <PersonOffRounded />
          </Box>
          <Box>
            <Typography sx={{ fontSize: "20px", fontWeight: 700 }}>
              Dar de baja al socio
            </Typography>
            <Typography sx={{ fontSize: "13px", opacity: 0.85 }}>
              Seleccioná el motivo para inactivar la cuenta
            </Typography>
          </Box>
        </Box>

        <PatientSummaryCard patient={patient} />

        <Formik
          onSubmit={(e) => {
            setReason("");
            handleClose(e);
          }}
          initialValues={{ reason: "", notify: true }}
          validate={(values) => {
            const errors = {};
            if (!values.reason || values.reason === "") {
              errors.reason = "Campo requerido";
            }
            return errors;
          }}
        >
          {({ isValid, dirty, setFieldValue, errors, values }) => {
            return (
              <Form>
                <FormControl fullWidth error={Boolean(errors.reason)}>
                  <InputLabel id="select-label">
                    Motivo de inactivación
                  </InputLabel>
                  <Select
                    labelId="select-label"
                    name="reason"
                    value={reason}
                    label="Motivo de inactivación"
                    onChange={(e) => {
                      setFieldValue("reason", e.target.value);
                      setReason(e.target.value);
                    }}
                    sx={{ borderRadius: "12px" }}
                  >
                    <MenuItem value={"INSURANCE_CANCELATION"}>
                      Baja del seguro
                    </MenuItem>
                    <MenuItem value={"UNCHARGEABLE"}>Incobrabilidad</MenuItem>
                    <MenuItem value={"EMISSION_ERROR"}>Error de emisión</MenuItem>
                    {reasons.map((r) => (
                      <MenuItem key={r.id} value={r.reason}>
                        {r.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.reason && (
                    <Typography
                      sx={{ color: "#DA2037", fontSize: "12px", mt: "6px" }}
                    >
                      {errors.reason}
                    </Typography>
                  )}
                </FormControl>

                <FormControlLabel
                  control={
                    <Checkbox
                      name="notify"
                      checked={values.notify}
                      onChange={(e) =>
                        setFieldValue("notify", e.target.checked)
                      }
                      sx={{
                        color: "#5a6470",
                        "&.Mui-checked": { color: "#002C54" },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: "14px", color: "#5a6470" }}>
                      Enviar notificación de baja al socio
                    </Typography>
                  }
                  sx={{ marginTop: "0.5rem" }}
                />

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.6rem",
                    background: "rgba(218,32,55,0.06)",
                    border: "1px solid rgba(218,32,55,0.15)",
                    borderRadius: "12px",
                    padding: "0.75rem 1rem",
                    marginTop: "1.25rem",
                  }}
                >
                  <WarningAmberRounded sx={{ color: "#DA2037" }} />
                  <Typography sx={{ fontSize: "13px", color: "#7a1620" }}>
                    El socio sigue activo hasta la fecha de baja: queda un
                    período de arrepentimiento durante el cual podés
                    reenviarle o usar el link de arrepentimiento desde el
                    listado.
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "0.75rem",
                    marginTop: "1.5rem",
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setReason("");
                      setOpen(false);
                    }}
                    sx={{
                      borderRadius: "20px",
                      textTransform: "none",
                      paddingX: "1.5rem",
                      color: "#5a6470",
                      borderColor: "#d4d8dd",
                      "&:hover": {
                        borderColor: "#b9bfc6",
                        background: "#f5f6f8",
                      },
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    disabled={!isValid || !dirty}
                    type="submit"
                    variant="contained"
                    startIcon={<PersonOffRounded />}
                    sx={{
                      backgroundColor: "#DA2037",
                      borderRadius: "20px",
                      textTransform: "none",
                      paddingX: "1.5rem",
                      boxShadow: "0 8px 20px rgba(218,32,55,0.28)",
                      "&:hover": { backgroundColor: "#e84353" },
                    }}
                  >
                    Confirmar baja
                  </Button>
                </Box>
              </Form>
            );
          }}
        </Formik>
      </Box>
    </BaseModal>
  );
};
